import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Reveal, Eyebrow } from "./Reveal";
import { CustomCutoffNote } from "./ChristmasCheckoutBanner";

const empty = { name: "", email: "", idea: "", colors: "", size: "", budget: "", deadline: "" };
const inputCls = "w-full rounded-2xl border-2 border-[var(--ink)] bg-white px-4 py-3 text-base outline-none focus:shadow-[4px_4px_0_var(--pink)] transition-shadow";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-semibold mb-1.5 text-sm">{label}</span>
    {children}
  </label>
);

export const CustomOrder = () => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/custom-requests", f);
      setDone(r.data?.reference ?? "received");
      setF(empty);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="custom" data-testid="custom-section" className="px-4 sm:px-8 py-20 sm:py-32 bg-[var(--pink)] border-b-2 border-[var(--ink)] relative overflow-hidden">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5 text-white">
          <Eyebrow color="var(--yellow)"><span className="text-[var(--ink)]">Custom prints</span></Eyebrow>
          <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
            Don't see exactly what you're looking for?
          </h2>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed max-w-md">
            Ask us about a custom print! Tell us your idea and we'll get back to you with a quote.
          </p>
          <ol className="mt-10 space-y-3 font-mono-x text-sm">
            {["Tell us your idea", "We reply with a quote", "We print it, you love it"].map((s, i) => (
              <li key={s} className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[var(--ink)] grid place-items-center font-bold">{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div data-testid="custom-success" className="rounded-[28px] chunk bg-[var(--paper)] p-8 sm:p-12 text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--pink)]" />
              <h3 className="font-display font-extrabold text-3xl mt-4">Request received!</h3>
              <p className="mt-3 text-[#3b3550]">Your reference is <span data-testid="custom-reference" className="font-mono-x font-bold text-[var(--ink)]">{done}</span>. We'll be in touch by email soon. Questions? Email <a href="mailto:scratchs3dprintz@gmail.com" className="underline">scratchs3dprintz@gmail.com</a></p>
              <button data-testid="custom-another-btn" onClick={() => setDone(null)} className="mt-8 press chunk-sm rounded-full bg-[var(--yellow)] px-6 py-3 font-bold">Send another idea</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="custom-order-form" className="rounded-[28px] chunk bg-[var(--paper)] p-6 sm:p-10 grid sm:grid-cols-2 gap-5">
              <CustomCutoffNote />
              <Field label="Your name *"><input required autoComplete="name" data-testid="custom-name-input" className={inputCls} value={f.name} onChange={set("name")} /></Field>
              <Field label="Email *"><input required type="email" inputMode="email" autoComplete="email" data-testid="custom-email-input" className={inputCls} value={f.email} onChange={set("email")} /></Field>
              <div className="sm:col-span-2">
                <Field label="What would you like printed? *">
                  <textarea required minLength={5} rows={5} data-testid="custom-idea-input" className={inputCls} value={f.idea} onChange={set("idea")}
                    placeholder="e.g. a name plate for my gaming desk with a little controller on it" />
                </Field>
              </div>
              <Field label="Colours"><input data-testid="custom-colors-input" className={inputCls} value={f.colors} onChange={set("colors")} placeholder="Purple + glow green" /></Field>
              <Field label="Approx. size"><input data-testid="custom-size-input" className={inputCls} value={f.size} onChange={set("size")} placeholder="About 10cm tall" /></Field>
              <Field label="Budget (NZD)"><input data-testid="custom-budget-input" className={inputCls} value={f.budget} onChange={set("budget")} placeholder="$20 - $50" /></Field>
              <Field label="Needed by"><input data-testid="custom-deadline-input" className={inputCls} value={f.deadline} onChange={set("deadline")} placeholder="Before Christmas" /></Field>
              {err && <p data-testid="custom-error" className="sm:col-span-2 text-[#b00040] font-semibold">{err}</p>}
              <button type="submit" disabled={busy} data-testid="custom-submit-btn"
                className="sm:col-span-2 press chunk rounded-full bg-[var(--ink)] text-[var(--paper)] font-bold py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? "Sending..." : <>Send my idea <Send className="w-5 h-5" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
